import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    Typography, Paper, Stack, Alert, Toolbar,
    Grid2,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import ModernNavbar from '../setup/ModernNavbar';
import AutoComplete from '../../BuiltInComponent/AutoComplete';
import Loading from '../../BuiltInComponent/Loading'
import HeadingText from '../../BuiltInComponent/HeadingTest';
import ButtonNorman from '../../BuiltInComponent/ButtonNormal'
import { getSalary, checkPeriodLock } from '../../features/PayrollManagement/salarySlice'
import { setOverTime } from '../../features/PayrollManagement/overTimeSlice'
import { getYear, getMonth } from '../../features/setup/useableSlice'
import { getUnits } from '../../features/AdminManagement/unitsSlice'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const pad2 = (n) => String(n).padStart(2, '0');

const minutesToHHMM = (mins) => {
    const m = Math.max(0, parseInt(mins || 0, 10));
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}:${pad2(mm)}`; // keep HH:MM
};

// Accepts: "H:MM", "HH:MM", "MM" (digits only -> minutes)
const hhmmToMinutes = (val) => {
    if (val == null) return 0;
    const raw = String(val).trim();
    if (raw === '') return 0;
    if (/^\d+$/.test(raw)) return parseInt(raw, 10); // digits only = minutes
    const m = raw.match(/^(\d{1,3}):(\d{1,2})$/);
    if (!m) return 0;
    const h = parseInt(m[1], 10) || 0;
    const mm = Math.min(59, Math.max(0, parseInt(m[2], 10) || 0));
    return h * 60 + mm;
};

export default function OvertimeAdjusterMUI() {

    const theme = useTheme()

    const cellStyle = {
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        textAlign: 'left',
        fontWeight: 'bolder',
        width: 20,
        textShadow: 'black 2px 2px 4px',
        cursor: 'pointer'
    }

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { years, months, isLoading } = useSelector(state => state.ru)
    const { units } = useSelector((state) => state.units)

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const rowsPerPage = 50;

    const [rptFilter, setRptFilter] = React.useState({
        selectedYear: null,
        selectedMonth: null,
        unitID: '',
        procOrder: 'Over Time',
        remarks: ''
    })

    const [periodLocked, setPeriodLocked] = React.useState(false)
    const [lockMessage, setLockMessage] = React.useState('')
    const unitOptions = [{ unitID: 'ALL', unitDescription: 'All Units' }, ...(units || [])]
    const canSubmit = Boolean(rptFilter.unitID) && rptFilter.unitID !== 'ALL' && !periodLocked

    const fetchData = async () => {
        if (!rptFilter.selectedYear || !rptFilter.selectedMonth || !rptFilter.unitID) {
            setData([])
            return;
        }

        setLoading(true);
        try {
            const salaryResponses = rptFilter.unitID === 'ALL'
                ? await Promise.all(
                    (units || []).map((unit) =>
                        dispatch(getSalary({
                            selectedYear: rptFilter.selectedYear,
                            selectedMonth: rptFilter.selectedMonth,
                            unitID: unit.unitID,
                            procOrder: 'Over Time'
                        })).unwrap()
                    )
                )
                : [await dispatch(getSalary({
                    selectedYear: rptFilter.selectedYear,
                    selectedMonth: rptFilter.selectedMonth,
                    unitID: rptFilter.unitID,
                    procOrder: 'Over Time'
                })).unwrap()];

            const modified = salaryResponses
                .flatMap((response) => response.salarySheet || [])
                .map((r) => {
                    const mins = parseInt(r.overtime_minutes || 0, 10);
                    const hhmm = r.overtime_hhmm && String(r.overtime_hhmm).includes(':')
                        ? r.overtime_hhmm.slice(0, 5)
                        : minutesToHHMM(mins);
                    const normalizedMinutes = hhmmToMinutes(hhmm);
                    return {
                        ...r,
                        overtime_minutes: normalizedMinutes,
                        overtime_hhmm: minutesToHHMM(normalizedMinutes),
                        edited: false,
                    };
                });
            setData(modified);
            setPage(1);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Replace with your API fetch
        // fetchData();
        dispatch(getYear())
        dispatch(getUnits())
    }, [dispatch]);

    useEffect(() => {
        if (!rptFilter.unitID && units?.length) {
            setRptFilter((prev) => ({ ...prev, unitID: 'ALL' }))
        }
    }, [units, rptFilter.unitID])

    const handleMinutesChange = (empID, value) => {
        const mins = Math.max(0, parseInt(value || 0, 10));
        setData((prev) =>
            prev.map((item) =>
                item.empID === empID
                    ? {
                        ...item,
                        overtime_minutes: mins,
                        overtime_hhmm: minutesToHHMM(mins),
                        edited: true,
                    }
                    : item
            )
        );
    };


    const handleHHMMChange = (empID, value) => {
        const mins = hhmmToMinutes(value);
        setData((prev) =>
            prev.map((item) =>
                item.empID === empID
                    ? {
                        ...item,
                        overtime_minutes: mins,
                        overtime_hhmm: minutesToHHMM(mins),
                        edited: true,
                    }
                    : item
            )
        );
    };


    const handleDeptChange = (data) => {
        const keys = Object.keys(data)
        if (keys[0] === 'yearID') {
            dispatch(getMonth({ year: data[keys] }))
            setRptFilter(prev => ({ ...prev, selectedYear: data[keys] }))
        }
        if (keys[0] === 'month_no') {
            setRptFilter(prev => ({ ...prev, selectedMonth: data[keys] }))
        }
        if (keys[0] === 'unitID') {
            setRptFilter(prev => ({ ...prev, unitID: data[keys] }))
        }
    }

    // Check if period is locked when filters change
    React.useEffect(() => {
        if (rptFilter.unitID === 'ALL') {
            setPeriodLocked(false)
            setLockMessage('')
            return
        }

        if (rptFilter.selectedYear && rptFilter.selectedMonth && rptFilter.unitID) {
            dispatch(checkPeriodLock({
                unitID: rptFilter.unitID,
                selectedYear: rptFilter.selectedYear,
                selectedMonth: rptFilter.selectedMonth
            })).then((res) => {
                if (res.payload?.isLocked) {
                    setPeriodLocked(true)
                    setLockMessage(res.payload?.message || 'Period is locked')
                } else {
                    setPeriodLocked(false)
                    setLockMessage('')
                }
            })
        }
    }, [rptFilter.selectedYear, rptFilter.selectedMonth, rptFilter.unitID, dispatch])

    const buttonClick = async () => {
        fetchData()
    }

    const handleSubmit = async () => {
        // const updated = data.filter((d) => d.edited);
        // console.log("Updated : ", updated)
        // console.log("Full Data : ", data)
        dispatch(setOverTime({ period: rptFilter, data: data })).then(() => {
            navigate('/DashBaord')
        })
        // if (updated.length === 0) return alert('No changes to submit');

        try {
            // setLoading(true);
            // await fetch('/api/overtime/update', {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify(updated),
            // });
            // alert('Updates submitted successfully');
            // setData((prev) => prev.map((d) => ({ ...d, edited: false })));
        } catch (err) {
            alert('Error while submitting');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (e, value) => {
        setPage(value);
    };

    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    const currentRows = data.slice(start, end);

    if (loading)
        return (
            <Stack alignItems="center" justifyContent="center" mt={5}>
                <CircularProgress />
            </Stack>
        );

    return (
        <>
            <ModernNavbar />
            <Toolbar />
            <Box p={3}>
                <Grid2 container spacing={2}>
                    <Grid2 size={2.5}></Grid2>
                    <Grid2 size={7}><HeadingText text={"Overtime Adjustment"} /></Grid2>
                    <Grid2 size={2.5}></Grid2>
                    <Grid2 size={2.5}>
                        {isLoading ? <><Loading /></> :
                            <>
                                <AutoComplete
                                    listData={years}
                                    columnName={'selectedYear'}
                                    label={'Year'}
                                    value={years.find((c) => c.yearID === rptFilter.selectedYear)}
                                    // value={emp.deptDesc}
                                    onChange={handleDeptChange}
                                />

                            </>}
                    </Grid2>
                    <Grid2 size={2.5}>
                        {isLoading ? <><Loading /></> :
                            <>
                                <AutoComplete
                                    listData={months}
                                    columnName={'selectedYear'}
                                    label={'Select Year'}
                                    value={months.find((c) => c.month_no === rptFilter.selectedMonth)}
                                    // value={emp.deptDesc}
                                    onChange={handleDeptChange}

                                />
                            </>}
                    </Grid2>
                    <Grid2 size={2.5}>
                        <AutoComplete
                            listData={unitOptions}
                            columnName={'unitID'}
                            label={'Unit'}
                            value={unitOptions.find((c) => c.unitID === rptFilter.unitID) || null}
                            onChange={handleDeptChange}
                        />
                    </Grid2>
                    <Grid2 size={2.5}>
                        <ButtonNorman text={"Get Data"} onClick={buttonClick} />
                    </Grid2>
                </Grid2>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={2} mb={2}>
                    <Button 
                        variant="contained" 
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        Submit Updated Overtime
                    </Button>
                </Stack>

                {rptFilter.unitID === 'ALL' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        View mode is showing overtime-enabled employees from all units. Select a single unit to save adjustments.
                    </Alert>
                )}

                {periodLocked && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>⛔ Period Locked:</strong> {lockMessage}. Cannot save overtime adjustments. Please delete the salary sheet first to unlock.
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={cellStyle}>#</TableCell>
                                <TableCell style={cellStyle}>Employee Name</TableCell>
                                <TableCell style={cellStyle}>Department</TableCell>
                                <TableCell style={cellStyle}>Unit</TableCell>
                                <TableCell style={cellStyle}>OT Allowed</TableCell>
                                <TableCell style={cellStyle}>OT Rate</TableCell>
                                <TableCell style={cellStyle}>Overtime (Minutes)</TableCell>
                                <TableCell style={cellStyle}>Overtime (HH:MM)</TableCell>
                                <TableCell style={cellStyle}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentRows.map((row, index) => (
                                <TableRow key={row.empID} sx={{ background: row.edited ? '#fff8e1' : 'inherit' }}>
                                    <TableCell>{start + index + 1}</TableCell>
                                    <TableCell>{row.empName}</TableCell>
                                    <TableCell>{row.deptName}</TableCell>
                                    <TableCell>{row.unitDescription}</TableCell>
                                    <TableCell>{row.empOT}</TableCell>
                                    <TableCell>{row.empOTRate}</TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={row.overtime_minutes}
                                            onChange={(e) => handleMinutesChange(row.empID, e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={row.overtime_hhmm}
                                            onChange={(e) => handleHHMMChange(row.empID, e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {row.edited ? (
                                            <Typography color="orange">Edited</Typography>
                                        ) : (
                                            <Typography color="gray">Original</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Stack direction="row" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(data.length / rowsPerPage)}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Stack>
            </Box >

        </>
    );
}
